import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, CheckCircle2, Gauge, LayoutTemplate, MessageSquareText, Search, Sparkles, Target, UsersRound, Workflow } from 'lucide-react';
import { AI_TOOLS, PLAN_ORDER, PLANS } from '../plans';
import GrowthLoop3D from '../components/GrowthLoop3D';
import ProspectCard3D from '../components/ProspectCard3D';
import { KeywordBars3D, RankGrid3D } from '../components/TrackerVisuals';
import { Reveal, TiltCard } from '../components/motion';

export function GrowthLoopSection() {
  return (
    <section className="services" id="platform">
      <div className="services-layout">
        <div className="services-copy">
          <Reveal className="section-header services-header">
            <div className="section-tag">THE CONNECTED GROWTH LOOP</div>
            <h2 className="section-title">From first search to paying customer.</h2>
            <p className="section-sub">One operating view for the work that usually gets scattered across forms, inboxes, spreadsheets, and disconnected apps.</p>
          </Reveal>
          <div className="services-grid">
            <Reveal as="article" className="service-item" delay={60}>
              <span className="service-number">01</span>
              <h3>Capture Demand</h3>
              <p>Launch conversion pages, lead forms, review links, QR campaigns, digital cards, and website chat.</p>
            </Reveal>
            <Reveal as="article" className="service-item" delay={120}>
              <span className="service-number">02</span>
              <h3>Respond &amp; Qualify</h3>
              <p>Reply instantly, recover missed calls, score fit, and route each opportunity to the right person.</p>
            </Reveal>
            <Reveal as="article" className="service-item" delay={180}>
              <span className="service-number">03</span>
              <h3>Convert &amp; Deliver</h3>
              <p>Manage appointments, estimates, pipeline stages, tasks, reminders, and customer intake from one workspace.</p>
            </Reveal>
            <Reveal as="article" className="service-item" delay={240}>
              <span className="service-number">04</span>
              <h3>Measure &amp; Improve</h3>
              <p>Connect ranking, campaign, response-time, source, conversion, reputation, and revenue reporting.</p>
            </Reveal>
          </div>
        </div>
        <Reveal className="services-visual" delay={120}>
          <GrowthLoop3D />
        </Reveal>
      </div>
    </section>
  );
}

export function VisibilitySection() {
  return (
    <section className="visibility-platform" id="local-visibility">
      <Reveal className="visibility-platform-head">
        <div><div className="section-tag">LOCAL VISIBILITY INTELLIGENCE</div><h2>See exactly where growth is getting stuck.</h2></div>
        <p>Track market visibility, save keyword evidence, and turn ranking gaps into a focused local-search improvement plan.</p>
      </Reveal>
      <div className="visibility-benefits">
        <TiltCard as="article" intensity={4} lift={3}><span>01</span><h3>See Where You Rank</h3><p>Use neighborhood-level tracking to visualize where the business appears in local search.</p></TiltCard>
        <TiltCard as="article" intensity={4} lift={3}><span>02</span><h3>Track Maps &amp; Search</h3><p>Compare keyword positions across Google Maps and traditional search to uncover visibility gaps.</p></TiltCard>
        <TiltCard as="article" intensity={4} lift={3}><span>03</span><h3>Scale SEO Efforts</h3><p>Centralize ranking records across the locations and markets you manage.</p></TiltCard>
        <TiltCard as="article" intensity={4} lift={3}><span>04</span><h3>Create Shareable Reports</h3><p>Turn saved performance data into clear evidence of keyword-level wins.</p></TiltCard>
      </div>
      <div className="tracker-comparison">
        <Reveal as="article"><RankGrid3D /><div><div className="section-tag">GRID RANK TRACKER</div><h3>Neighborhood-level map visibility</h3><p>Track target keywords across a consistent grid and uncover pockets where local competitors outrank the business.</p><Link to="/signup">Open rank workspace →</Link></div></Reveal>
        <Reveal as="article" delay={90}><KeywordBars3D /><div><div className="section-tag">KEYWORD RANK TRACKER</div><h3>One view across Maps and Search</h3><p>Record positions by keyword and market, compare channels, and focus optimization on the largest ranking gaps.</p><Link to="/signup">Start tracking →</Link></div></Reveal>
      </div>
    </section>
  );
}

export function ProspectSection() {
  return (
    <section className="prospect-intelligence">
      <div className="prospect-top">
        <Reveal className="prospect-copy">
          <div className="section-tag">PROSPECT INTELLIGENCE</div>
          <h2>Beyond basic contact data.</h2>
          <p>Stop chasing cold contacts. Build contextual sales conversations around SEO gaps, weak reviews, missing social presence, and website performance.</p>
          <Link to="/signup" className="btn-primary">Build a lead list <ArrowRight size={16} /></Link>
        </Reveal>
        <Reveal className="prospect-visual" delay={110}>
          <ProspectCard3D />
        </Reveal>
      </div>
      <div className="prospect-features">
        <TiltCard as="article" intensity={5} lift={5}><span className="prospect-feature-icon"><Gauge size={18} /></span><h3>360° Prospect Intelligence</h3><p>Unify SEO health, reviews, social presence, location, and website data in one lead profile.</p></TiltCard>
        <TiltCard as="article" intensity={5} lift={5}><span className="prospect-feature-icon"><Target size={18} /></span><h3>Smart Sales Positioning</h3><p>Turn digital blind spots into specific, credible reasons to start a conversation.</p></TiltCard>
        <TiltCard as="article" intensity={5} lift={5}><span className="prospect-feature-icon"><Search size={18} /></span><h3>Filter By Multiple Parameters</h3><p>Build precise lists around industry, market, digital maturity, reviews, and social readiness.</p></TiltCard>
        <TiltCard as="article" intensity={5} lift={5}><span className="prospect-feature-icon"><UsersRound size={18} /></span><h3>Full-Funnel Lead Profiles</h3><p>Keep prospect context organized from first discovery through qualification and close.</p></TiltCard>
      </div>
    </section>
  );
}

const AI_DEMOS = {
  website_builder: { icon: LayoutTemplate, label: 'Launch', metric: 'Downloadable HTML', headline: 'A conversion-ready site before lunch.', summary: 'Turn an offer, audience, and market into structured homepage copy, trust sections, calls to action, and a responsive HTML draft.', steps: ['Hero and offer', 'Services and proof', 'Lead capture path'] },
  campaign_writer: { icon: MessageSquareText, label: 'Growth', metric: 'Email + text', headline: 'One offer. A coordinated campaign.', summary: 'Generate useful email and text follow-up around a clear customer outcome, then save the campaign into the account workspace.', steps: ['Campaign angle', 'Message sequence', 'Conversion path'] },
  workflow_builder: { icon: Workflow, label: 'Business', metric: 'Triggers + actions', headline: 'Turn follow-up into an operating system.', summary: 'Map triggers, conditions, ownership, messages, tasks, and escalation rules before activating an automation.', steps: ['Choose trigger', 'Route and respond', 'Escalate exceptions'] },
  funnel_auditor: { icon: BarChart3, label: 'Revenue Partner', metric: 'Full-funnel review', headline: 'Find where opportunity becomes leakage.', summary: 'Review acquisition, response speed, sales conversion, and retention to prioritize the next operational improvement.', steps: ['Source quality', 'Response speed', 'Revenue conversion'] },
};

export function AIProductShowcase() {
  const [active, setActive] = useState('website_builder');
  const demo = AI_DEMOS[active];
  const Icon = demo.icon;
  return (
    <section className="ai-product-showcase" id="ai-studio">
      <Reveal className="ai-showcase-head"><div><div className="section-tag">AI GROWTH STUDIO</div><h2>Useful drafts, built from real business context.</h2></div><p>Fifteen focused assistants help teams build pages, campaigns, responses, forms, workflows, and revenue plans. Every draft stays attached to the account that created it.</p></Reveal>
      <div className="ai-showcase-stage">
        <div className="ai-showcase-nav">{Object.entries(AI_DEMOS).map(([key, item]) => { const DemoIcon = item.icon; return <button type="button" className={active === key ? 'active' : ''} onClick={() => setActive(key)} key={key}><DemoIcon size={18} /><span><strong>{AI_TOOLS.find(tool => tool.id === key)?.name}</strong><small>{item.label}</small></span></button>; })}<Link to="/signup?plan=launch" className="ai-showcase-cta">Open the AI Studio <ArrowRight size={16} /></Link></div>
        <div className="ai-showcase-output"><header><span><Icon size={20} /></span><div><small>{demo.label} workspace</small><strong>{demo.metric}</strong></div><b>Draft ready</b></header><div className="ai-showcase-copy"><span>Generated outcome</span><h3>{demo.headline}</h3><p>{demo.summary}</p></div><div className="ai-showcase-steps">{demo.steps.map((step, index) => <div key={step}><span>0{index + 1}</span><strong>{step}</strong><CheckCircle2 size={16} /></div>)}</div><footer><span>Saved to workspace</span><span>Review before publishing</span></footer></div>
      </div>
    </section>
  );
}

export function FreeToolsSection() {
  return (
    <section className="free-tools" id="free-tools">
      <Reveal className="free-tools-intro">
        <div className="section-tag"> FREE TOOLS</div>
        <h2 className="section-title">Know where you stand.</h2>
        <p>Run focused checks across search readiness, metadata, schema, mobile usability, and site performance.</p>
        <Link to="/free-tools#visibility-scan" className="btn-primary">Run the Free Visibility Scan</Link>
      </Reveal>
      <div className="tools-list">
        <Link id="website-visibility-scan" to="/free-tools#visibility-scan">Live Website Visibility Scan <span>Free</span></Link>
        <Link id="schema-validator" to="/free-tools#visibility-scan">Mobile, Metadata &amp; Schema Check <span>Free</span></Link>
        <Link id="local-rank-checker" to="/dashboard">Grid Rank Tracker <span>Account</span></Link>
        <Link id="keyword-rank-tracker" to="/dashboard">Keyword Rank Tracker <span>Account</span></Link>
        <Link id="prospect-intelligence-tool" to="/dashboard">Prospect Intelligence <span>Account</span></Link>
        <Link id="optimization-workspace" to="/dashboard">Optimization &amp; Setup Guides <span>Account</span></Link>
      </div>
    </section>
  );
}

function ProductSuiteCatalog() {
  return (
    <div className="product-suite-catalog">
      {PLAN_ORDER.map((planId, planIndex) => {
        const plan = PLANS[planId];
        const tools = plan.features.filter(tool => !tool.startsWith('Everything in ') && !/^\d+ users?$/.test(tool));
        const aiTools = AI_TOOLS.filter(tool => tool.minimum === planId);
        return <TiltCard as="section" className="product-suite-tier" intensity={3} lift={3} key={planId}>
          <header className="product-suite-tier-head">
            <span>0{planIndex + 1}</span>
            <div><small>Available with</small><h3>{plan.name}</h3></div>
            <b>{tools.length + aiTools.length + (plan.service?.length || 0)} capabilities</b>
          </header>
          <div className="product-suite-tools">
            {tools.map(tool => <article key={tool}><CheckCircle2 size={17} /><span>{tool}</span></article>)}
            {aiTools.map(tool => <article className="ai-capability" key={tool.id}><Sparkles size={17} /><span>{tool.name}<small>AI Studio</small></span></article>)}
            {plan.service?.map(service => <article className="managed-service" key={service}><CheckCircle2 size={17} /><span>{service}<small>Monthly optimization review</small></span></article>)}
          </div>
        </TiltCard>;
      })}
    </div>
  );
}

export function ProductSuiteSection() {
  return (
    <section className="ventures" id="ventures">
      <Reveal className="section-header">
        <div className="section-tag">COMPLETE TOOL STACK</div>
        <h2 className="section-title">The Product Suite</h2>
        <p className="section-sub">Every customer-facing, operational, reporting, automation, and AI-assisted capability, organized by the plan that unlocks it.</p>
      </Reveal>
      <ProductSuiteCatalog />
    </section>
  );
}

export function OperatingModelSection() {
  return (
    <section className="operating-model" id="how-it-works">
      <Reveal className="operating-model-head">
        <div><div className="section-tag">HOW DATA TALLIED WORKS</div><h2>One customer record.<br /><span>Every next step.</span></h2></div>
        <p>Source, intent, conversation, ownership, and outcome stay connected. Teams can see what needs attention without rebuilding the story in another spreadsheet.</p>
      </Reveal>
      <div className="operating-flow">
        {[
          { icon: LayoutTemplate, step: '01', label: 'Capture', title: 'Demand enters with context', copy: 'Pages, forms, chat, calls, QR codes, and campaigns retain the source and requested service.' },
          { icon: MessageSquareText, step: '02', label: 'Respond', title: 'The right reply goes out', copy: 'Use saved templates or an AI-assisted draft, assign an owner, and keep the conversation attached to the lead.' },
          { icon: Workflow, step: '03', label: 'Convert', title: 'The next action stays visible', copy: 'Move opportunities through appointments, estimates, tasks, reminders, intake, and sales stages.' },
          { icon: BarChart3, step: '04', label: 'Improve', title: 'Outcomes shape the next move', copy: 'Compare sources, response speed, campaigns, reviews, rankings, and revenue to choose the next improvement.' },
        ].map(item => { const Icon = item.icon; return <TiltCard as="article" key={item.step} intensity={3.5} lift={0}><header><span>{item.step}</span><Icon size={20} /></header><small>{item.label}</small><h3>{item.title}</h3><p>{item.copy}</p></TiltCard>; })}
      </div>
      <div className="operating-model-foot"><div><strong>Start with the part of the loop you need now.</strong><span>Each plan keeps lower-tier tools and adds the next operating layer.</span></div><Link to="/pricing" className="btn-primary">Compare growth plans <ArrowRight size={16} /></Link></div>
    </section>
  );
}
